import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {map, Observable, switchMap} from 'rxjs';
import {GetResult, QueryResult, RemitApiResponse} from '..';
import {QINVENTORY_BASE_URL} from './config';
import {DigitalProductDetails, RequiredFields, ShopItemVariant} from './remit-shop.service';

export interface RemitInventoryLocationCreateForm{
  name: string
  shopCode: string
}

export interface RemitInventoryLocation{
  id: string
  name: string
  shopCode: string
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
}

export interface StockBatchModifyForm{
  locationId: string
  items: InventoryItemForm[]
}

export interface StockBatchTransferForm{
  sourceId: string
  destinationId: string
  items: InventoryItemForm[]
}

export interface InventoryItemForm{
  sku: string
  quantity: number
}

export interface StockInfo{
  id: string
  productId: string
  locationId: string
  quantity: number
  createdAt?: string
  createdBy?: string
  updatedAt?: string
  updatedBy?: string
}

export interface StockTransferInfo{
  id: string,
  items: Partial<StockInfo>[],
  sourceId: string,
  destinationId: string,
  createdAt: string,
  createdBy: string,
  updatedAt: string,
  updatedBy: string,
  acceptedAt: string | null,
  acceptedBy: string,
  note: string,
  deletedAt: string | null,
  deletedBy: string,
  version: string
  itemViews?: ItemView[]
}

export interface StockTransferTransactionQueryParams{
  pageNumber?: number | null
  pageSize?: number | null
  sortBy?: string | null
  sortDirection?: number | null
  status?: string[] | null
  sourceId?: string | null
  destinationId?: string | null
  search?: string | null
}


export interface SupplierForm{
  name?: string // required
  contactPerson?: string
  telp1?: string
  telp2?: string
  fax?: string
  address?: string
  email?: string
}

export interface Supplier{
  id: string
  name: string
  contactPerson?: string
  telp1?: string
  telp2?: string
  fax?: string
  address?: string
  email?: string
}

export interface SupplierQueryParams{
  pageNumber?: number | null
  pageSize?: number | null
}

export interface PurchaseOrderQueryParams{
  pageNumber?: number | null
  pageSize?: number | null
  sortBy?: string | null
  sortDirection?: number | null
  supplierName?: string | null
  locationName?: string | null
  productName?: string | null
  note?: string | null
  locationId?: string | null
  sku?: string | null
  skus?: string[] | null
  createdAtStart?: string | null
  createdAtEnd?: string | null
}


export interface PurchaseOrderForm{
  supplierId: string
  locationId: string
  items: Partial<PurchaseOrderItem>[]
  note?: string
}


export interface PurchaseOrder{
  id: string
  supplierId: string
  locationId: string
  items?: Partial<PurchaseOrderItem>[]
  note?: string
  address?: string
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
  shopCode?: string
  locationName?: string
  supplierName?: string
  cancelAt?: string
  cancelBy?: string
  reason?: string
  version?: string
  itemsView?: PurchaseOrderItemView[]
}

export interface PurchaseOrderItem{
  sku: string
  quantity: number
  price: number
}

export interface PurchaseOrderItemView{
  sku: string
  quantity: number
  price: number
  productId: string
  productName: string
}

export interface TransferItem{
  sku: string
  quantity: number
}

export interface StockTransferRequest{
  sourceId: string
  destinationId: string
  transferItems: TransferItem[]
  note?: string
}

interface ProductDetails {
  id: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  name: string;
  description: string;
  shortDescription: string;
  price: number;
  capitalPrice: number;
  isDeleted: boolean;
  image: string;
  vipDurationDays: number;
  shippingCost: number;
  itemType: string;
  digitalProductDetails?: DigitalProductDetails | null
  soldOut: boolean;
  version: string;
  tags: string[];
  voucherCount: number;
  originalPrice: number;
  sku: string;
  variant: ShopItemVariant[];
  notes: string;
  barcodes: string[];
  requiredFields: RequiredFields[] | null;
  available: boolean;
}

interface ItemView {
  productId: string;
  quantity: number;
  sku: string;
  details: ProductDetails;
}

export interface ItemViews {
  itemViews: ItemView[];
}


export interface InventoryQuantityQueryParams{
  pageNumber?: number | null
  pageSize?: number | null
  sortBy?: string | null
  sortDirection?: number | null
}


@Injectable({
  providedIn: 'root',
})
export class RemitShopInventoryService {
  constructor(
    private http: HttpClient,
    @Inject(QINVENTORY_BASE_URL) private remitApiUrl: string,
  ) { }

  createInventoryLocation(data: Partial<RemitInventoryLocationCreateForm>):
  Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/location',
        data,
    );
  }

  getInventoryLocation(id: string): Observable<RemitInventoryLocation> {
    return this.http.get<{body: RemitInventoryLocation}>(
        this.remitApiUrl + '/location/' + id,
    ).pipe(map((v)=> v.body));
  }


  updateInventoryLocation(
      id: string, data: Partial<RemitInventoryLocationCreateForm>):
  Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + '/location/' + id,
        data,
    );
  }

  queryInventoryLocation(params: {
    pageNumber: number
    pageSize: number
    sortBy?: string
    sortDirection?: number | null
    isShown?: boolean | null
  }): Observable<QueryResult<RemitInventoryLocation>> {
    return this.http.post<QueryResult<RemitInventoryLocation>>(
        this.remitApiUrl + '/locations',
        params,
    );
  }

  getLocationId(shopCode: string): Observable<string> {
    return this.queryInventoryLocation({
      pageNumber: 0, pageSize: 100,
    }).pipe(map((res)=>{
      return res.body?.find((v)=> v.shopCode == shopCode)?.id ?? '';
    }));
  }

  addStock(params:{
    locationId: string,
    sku: string,
    quantity: number,
  }):Observable<RemitApiResponse> {
    return this.increaseStockBatch({
      locationId: params.locationId,
      items: [{
        sku: params.sku,
        quantity: params.quantity,
      }],
    });
  }

  increaseStockBatch(data: StockBatchModifyForm):
  Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/trx/add',
        data,
    );
  }


  removeStock(params:{
    locationId: string,
    sku: string,
    quantity: number,
  }):Observable<RemitApiResponse> {
    return this.decreaseStockBatch({
      locationId: params.locationId,
      items: [{
        sku: params.sku,
        quantity: params.quantity,
      }],
    });
  }

  decreaseStockBatch(data: StockBatchModifyForm):
  Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/trx/remove',
        data,
    );
  }

  setStock(params:{
    locationId: string,
    sku: string,
    quantity: number,
  }):Observable<RemitApiResponse> {
    return this.setStockBatch({
      locationId: params.locationId,
      items: [{
        sku: params.sku,
        quantity: params.quantity,
      }],
    });
  }

  setStockBatch(data: StockBatchModifyForm):
  Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/trx/set',
        data,
    );
  }

  transferStockBatch(data: StockBatchTransferForm):
  Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/trx/transfer',
        data,
    );
  }

  getInventoryQuantity(locationId: string, sku: string):
  Observable<StockInfo> {
    return this.http.get<{body: StockInfo}>(
        this.remitApiUrl + '/stock/' + sku + '/location/' + locationId,
    ).pipe(map((v)=> v.body));
  }

  queryInventoryQuantity(params: InventoryQuantityQueryParams):
  Observable<QueryResult<StockInfo>> {
    return this.http.post<QueryResult<StockInfo>>(
        this.remitApiUrl + '/stocks',
        params,
    );
  }

  refreshInventory(locationId: string, sku: string):Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/stock/' + sku + '/location/' + locationId+'/refresh',
        {},
    );
  }

  createSupplier(data: Partial<SupplierForm>): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/supplier',
        data,
    );
  }

  getSupplier(id: string): Observable<Supplier> {
    return this.http.get<{body: Supplier}>(
        this.remitApiUrl + '/supplier/' + id,
    ).pipe(map((v)=> v.body));
  }

  updateSupplier(id: string, data: Partial<SupplierForm>):
  Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + '/supplier/' + id,
        data,
    );
  }

  deleteSupplier(id: string): Observable<RemitApiResponse> {
    return this.http.delete<RemitApiResponse>(
        this.remitApiUrl + '/supplier/' + id,
    );
  }

  querySuppliers(params: SupplierQueryParams):
  Observable<QueryResult<Supplier>> {
    return this.http.post<QueryResult<Supplier>>(
        this.remitApiUrl + '/suppliers',
        params,
    );
  }

  createPurchaseOrder(data: Partial<PurchaseOrderForm>):
  Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/purchasing-order',
        data,
    );
  }

  getPurchaseOrder(id: string): Observable<PurchaseOrder> {
    return this.http.get<{body: PurchaseOrder}>(
        this.remitApiUrl + '/purchasing-order/' + id,
    ).pipe(map((v)=> v.body));
  }

  cancelPurchaseOrder(id: string, param:{
    reason: string
  }): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/purchasing-order/' + id + '/cancel',
        param,
    );
  }

  undoCancelPurchaseOrder(id: string): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/purchasing-order/' + id + '/undo-cancel',
        {},
    );
  }


  queryPurchaseOrders(
      params: PurchaseOrderQueryParams,
  ): Observable<QueryResult<PurchaseOrder>> {
    return this.http.post<QueryResult<PurchaseOrder>>(
        this.remitApiUrl + '/purchasing-orders',
        params,
    );
  }

  updatePurchaseOrder(id: string, data: Partial<PurchaseOrderForm>):
  Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + '/purchasing-order/' + id,
        data,
    );
  }

  queryStockTransfers(
      params: StockTransferTransactionQueryParams,
  ): Observable<QueryResult<StockTransferInfo>> {
    const hasSearch = params.search && params.search.trim() !== '';

    if (!hasSearch) {
      // Use server-side filtering if no search term
      return this.http.post<QueryResult<StockTransferInfo>>(
          this.remitApiUrl + '/stock-transfers',
          params,
      );
    } else {
      // If search term exists, first get total count then query all items
      const searchTerm = params.search?.toLowerCase() || '';
      const countParams = {...params, search: null, pageNumber: 0, pageSize: 1};

      return this.http.post<QueryResult<StockTransferInfo>>(
          this.remitApiUrl + '/stock-transfers',
          countParams,
      ).pipe(
          switchMap((countResult) => {
          // Get the total count from the response or default to a large number
            const maxPageSize = countResult.totalData ?? 1000;
            return this.http.post<QueryResult<StockTransferInfo>>(
                this.remitApiUrl + '/stock-transfers',
                {
                  ...params,
                  search: null,
                  pageNumber: 0,
                  pageSize: maxPageSize, // Use a large fixed page size
                },
            ).pipe(
                map((result) => {
                  const filteredItems = result.body?.filter((item) =>
                    item.itemViews?.some((iv) =>
                      iv.details.name.toLowerCase().includes(searchTerm),
                    ) || false,
                  ) || [];

                  // Calculate pagination for filtered results
                  const pageSize = params.pageSize || 10;
                  const pageNumber = params.pageNumber || 0;
                  const startIndex = pageNumber * pageSize;
                  const paginatedItems = filteredItems.slice(
                      startIndex,
                      startIndex + pageSize,
                  );

                  return {
                    ...result,
                    body: paginatedItems,
                    totalCount: filteredItems.length,
                  };
                }),
            );
          }),
      );
    }
  }

  createStockTransfer(payload: StockTransferRequest): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/stock-transfer',
        payload,
    );
  }

  acceptStockTransfer(id: string): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + `/stock-transfer/${id}/accept`,
        {},
    );
  }

  deleteStockTransfer(id: string): Observable<RemitApiResponse> {
    return this.http.delete<RemitApiResponse>(
        this.remitApiUrl + '/stock-transfer/' + id,
    );
  }

  undoStockTransferDelete(id: string): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/stock-transfer/' + id + '/undo-delete',
        {},
    );
  }

  updateStockTransfer(id: string, payload: StockTransferRequest):
  Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + '/stock-transfer/' + id,
        payload,
    );
  }

  getStockTransfer(id: string): Observable<StockTransferInfo> {
    return this.http.get<GetResult<StockTransferInfo>>(
        this.remitApiUrl + '/stock-transfer/' + id,
    ).pipe(map((v)=> v.body));
  }
}
